import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { buildApiUrl } from "../lib/api-client";

export default function RegisterPage(): JSX.Element {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { refreshSession } = useAuth();

    const validatePassword = (): boolean => {
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return false;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validatePassword()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(buildApiUrl("/auth/register"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.error || "Registration failed");
            }

            await refreshSession();
            navigate("/");
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const passwordStrength = (): { label: string; color: string; width: string } => {
        if (password.length === 0) return { label: "", color: "", width: "0%" };
        if (password.length < 8) return { label: "Weak", color: "#dc2626", width: "33%" };
        if (password.length < 12) return { label: "Medium", color: "#f59e0b", width: "66%" };
        return { label: "Strong", color: "#10b981", width: "100%" };
    };

    const strength = passwordStrength();

    return (
        <div className="auth-container">
            <div className="auth-card animate-slide-up">
                <div className="auth-header">
                    <div className="logo-container">
                        <svg
                            className="logo-icon"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                x="8"
                                y="8"
                                width="32"
                                height="32"
                                rx="8"
                                fill="url(#gradient)"
                            />
                            <path
                                d="M16 20h16M16 24h12M16 28h16"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                            <defs>
                                <linearGradient
                                    id="gradient"
                                    x1="8"
                                    y1="8"
                                    x2="40"
                                    y2="40"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stopColor="var(--color-primary)" />
                                    <stop offset="1" stopColor="var(--color-accent)" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <h1>Create Account</h1>
                    <p className="auth-subtitle">Start building beautiful forms today</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="auth-error animate-fade-in">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm1 12H7v-2h2v2zm0-3H7V4h2v5z" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            autoComplete="username"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            autoComplete="new-password"
                            disabled={isLoading}
                            minLength={8}
                        />
                        {password.length > 0 && (
                            <div className="password-strength">
                                <div className="strength-bar">
                                    <div
                                        className="strength-fill"
                                        style={{
                                            width: strength.width,
                                            backgroundColor: strength.color,
                                        }}
                                    />
                                </div>
                                <span style={{ color: strength.color }}>{strength.label}</span>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            id="confirmPassword"
                            name="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            autoComplete="new-password"
                            disabled={isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="button primary auth-submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner" />
                                Creating account...
                            </>
                        ) : (
                            "Create Account"
                        )}
                    </button>

                    <p className="terms-text">
                        By creating an account, you agree to our{" "}
                        <a href="/terms" target="_blank" rel="noopener noreferrer">
                            Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </a>
                    </p>
                </form>

                <div className="auth-footer">
                    <p>
                        Already have an account?{" "}
                        <Link to="/login" className="auth-link">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>

            <style>{`
        .password-strength {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }

        .strength-bar {
          flex: 1;
          height: 4px;
          background: var(--color-border);
          border-radius: 2px;
          overflow: hidden;
        }

        .strength-fill {
          height: 100%;
          transition: all 0.3s ease;
        }

        .password-strength span {
          font-size: 0.75rem;
          font-weight: 500;
          min-width: 60px;
        }

        .terms-text {
          font-size: 0.75rem;
          color: var(--color-text-muted);
          text-align: center;
          margin-top: 0.5rem;
        }

        .terms-text a {
          color: var(--color-primary);
          text-decoration: underline;
        }

        .terms-text a:hover {
          color: var(--color-primary-hover);
        }
      `}</style>
        </div>
    );
}
